import {
    faChevronDown,
    faChevronUp,
    faMinusCircle,
    faPlusCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { WikiArticleEntity } from "@/mk2/apps/wiki/schemas";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { StarsRating } from "@/mk2/components/StarsRating";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { dateLocal } from "@/mk2/helpers/timeformat";
import {
    WIKI_AI_REVIEW_SUMMARY_BADGE,
    WIKI_AI_REVIEW_SUMMARY_DISCLAIMER,
    WIKI_AI_REVIEW_SUMMARY_HIDE,
    WIKI_AI_REVIEW_SUMMARY_NEGATIVES,
    WIKI_AI_REVIEW_SUMMARY_POSITIVES,
    WIKI_AI_REVIEW_SUMMARY_SHOW,
    WIKI_EXP_AVERAGE_SCORE,
} from "@/mk/autogenerated/translations/AIReviewSummaryCard.a61129fd1b7b233c712f0e45d4f0942f";

import styles from "./AIReviewSummaryCard.mscss";

interface OwnProps {
    article: WikiArticleEntity;
}

interface State {
    isExpanded: boolean;
}

/**
 * AI-generated summary of all reviews of a product, shown at the very top of a
 * review page (above the gallery): average star rating, an "average
 * description" paragraph and the most-common pros/cons. Collapsible like the
 * forum AIThreadSummaryCard. Renders nothing until a summary has been published.
 */
export default class AIReviewSummaryCard extends React.Component<OwnProps, State> {
    public state: State = { isExpanded: false };

    private toggle = () => {
        this.setState((s) => ({ isExpanded: !s.isExpanded }));
    };

    private handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggle();
        }
    };

    public render() {
        const { article } = this.props;
        if (!article.aiReviewSummaryAST) {
            return null;
        }

        const { isExpanded } = this.state;
        const positives = article.aiReviewSummaryPositives ?? [];
        const negatives = article.aiReviewSummaryNegatives ?? [];

        const additionalData = {
            ...defaultFilterAdditionalData,
            renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
            renderSpecialWikiLinks: false,
            linkedObjects: article.aiReviewSummaryASTAdditionalData?.linkedObjects || {},
            photos: [],
            rootDiv: false,
            hashizeFn: (chunk) => chunk,
        };

        return (
            <div
                className={cx(
                    styles.AIReviewSummaryCard,
                    isExpanded && styles["AIReviewSummaryCard--expanded"],
                )}
                role="region"
                aria-label={WIKI_AI_REVIEW_SUMMARY_BADGE}
                data-ai-generated="true"
                data-ai-generated-at={article.aiReviewSummaryUpdatedAt || undefined}
            >
                <div className={styles.AIReviewSummaryCard__header}>
                    <span className={styles.AIReviewSummaryCard__badge}>
                        {WIKI_AI_REVIEW_SUMMARY_BADGE}
                    </span>
                    {article.experiencesStarsAllowed && article.experiencesScore != null && (
                        <StarsRating
                            starsCount={article.experiencesScore}
                            canShowHalf
                            label={WIKI_EXP_AVERAGE_SCORE}
                            className={styles.AIReviewSummaryCard__stars}
                        />
                    )}
                </div>

                <div className={styles.AIReviewSummaryCard__intro}>
                    {WIKI_AI_REVIEW_SUMMARY_DISCLAIMER}
                    {article.aiReviewSummaryUpdatedAt && (
                        <> &middot; {dateLocal(article.aiReviewSummaryUpdatedAt)}</>
                    )}
                </div>

                <div
                    className={cx(
                        styles.AIReviewSummaryCard__body,
                        !isExpanded && styles["AIReviewSummaryCard__body--collapsed"],
                    )}
                >
                    {astToHtml(article.aiReviewSummaryAST, additionalData)}

                    {positives.length > 0 && (
                        <ul className={styles.AIReviewSummaryCard__positivesList}>
                            <li className={styles.AIReviewSummaryCard__listHeader}>
                                {WIKI_AI_REVIEW_SUMMARY_POSITIVES}
                            </li>
                            {positives.map((point, index) => (
                                <li className={styles.AIReviewSummaryCard__listItem} key={index}>
                                    <div
                                        className={
                                            styles.AIReviewSummaryCard__listItem__iconContainer
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                    </div>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    )}

                    {negatives.length > 0 && (
                        <ul className={styles.AIReviewSummaryCard__negativesList}>
                            <li className={styles.AIReviewSummaryCard__listHeader}>
                                {WIKI_AI_REVIEW_SUMMARY_NEGATIVES}
                            </li>
                            {negatives.map((point, index) => (
                                <li className={styles.AIReviewSummaryCard__listItem} key={index}>
                                    <div
                                        className={
                                            styles.AIReviewSummaryCard__listItem__iconContainer
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMinusCircle} />
                                    </div>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div
                    className={styles.AIReviewSummaryCard__bar}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={this.toggle}
                    onKeyDown={this.handleKeyDown}
                >
                    <span className={styles.AIReviewSummaryCard__hint}>
                        {isExpanded ? WIKI_AI_REVIEW_SUMMARY_HIDE : WIKI_AI_REVIEW_SUMMARY_SHOW}
                    </span>
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className={styles.AIReviewSummaryCard__chevron}
                    />
                </div>
            </div>
        );
    }
}
